import { useEffect, type ReactElement, type ReactNode, type VFC } from 'react';

import classNames from 'classnames';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { CnpAuthor, CnpAutorId } from '@vgtv/api-client/lib/cnp_authors';

import { Title } from '../Asset/shared/Title/Title';
import { TagsList } from '../TagsList/TagsList';
import { ExpandButton } from '../ExpandButton/ExpandButton';
import { useExpanded } from '../utils/useExpanded';
import { ChaptersList } from '../ChaptersList/ChaptersList';
import { ShareButton } from '../ShareButton/ShareButton';
import { useViewportType } from '../utils/useViewportType';
import type { SvpTag } from '../../shared/helpers/assetHelpers';
import { Label } from '../Label/Label';
import { Bylines } from '../Bylines/Bylines';
import { useAuthorsGet } from '../Bylines/useAuthorsGet';
import { useBylineTracking } from '../AssetMetadata/useBylineTracking';

import * as styles from './AssetDetails.module.scss';

export interface AssetDetailsProps {
  title: SvpAssetAdditional['title'];
  labels?: ReactNode[];
  shareUrl?: string;
  description?: SvpAssetAdditional['description'];
  tags?: readonly SvpTag[];
  chapters?: SvpAssetAdditional['additional']['chapters'];
  metadata?: ReactElement;
  authorIds?: string;
}

const defaultTags: SvpTag[] = [];
const defaultLabels: string[] = [];
const defaultChapters: SvpAssetAdditional['additional']['chapters'] = [];

const AssetDetails: VFC<AssetDetailsProps> = ({
  shareUrl,
  title,
  labels = defaultLabels,
  metadata,
  description,
  tags = defaultTags,
  chapters = defaultChapters,
  authorIds,
}) => {
  const { isExpanded, onExpandButtonClick } = useExpanded(false);
  const { authors, areAuthorsFetching } = useAuthorsGet({ authorIds });
  const { isMobile } = useViewportType();

  const isCollapsed = isMobile && !isExpanded;
  const displayByline =
    !isCollapsed && !areAuthorsFetching && authors.length > 0;

  const displayChapters = Boolean(chapters.length);
  const displayTags = !isCollapsed && Boolean(tags.length);

  /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  return (
    <div
      className={classNames(styles.container, {
        [styles.isCollapsed]: isCollapsed,
      })}
      onClick={isCollapsed ? onExpandButtonClick : undefined}
    >
      {/* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      {displayChapters && (
        <div className={styles.chapters}>
          <div className={styles.contentWrapper}>
            <ChaptersList chapters={chapters} />
          </div>
        </div>
      )}
      <div className={styles.contentWrapper}>
        <div className={styles.titleWrapper}>
          <Title title={title} className={styles.title} rootElement="h1" />
          <ShareButton title={title} url={shareUrl} />
        </div>
        <div className={styles.subtitle}>
          <Label labels={labels} />
        </div>

        <p className={styles.description}>{description}</p>
        {displayByline && <Bylines authors={authors} />}
        {metadata}
        {displayTags && (
          <div className={styles.tags}>
            <TagsList tags={tags} />
          </div>
        )}
        {isCollapsed && (
          <ExpandButton
            className={styles.expandButton}
            isExpanded={isExpanded}
            onClick={onExpandButtonClick} // repeat this here, so that we have a focusable element for a11ly
          />
        )}
      </div>
    </div>
  );
};

export { AssetDetails };
